import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTableEnhanced, cycleColumnSort } from '@/components/ui/data-table-enhanced';
import type { ColumnDef, ColumnSort } from '@/components/ui/data-table-enhanced';
import { MultiSelect } from '@/components/ui/multiselect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertCircle, CheckCircle2, Clock, Zap, Calendar, XCircle, ChevronDownIcon, ListChecks, Lock, Download, Loader2, Search, X, Wallet, TrendingDown, PenLine } from 'lucide-react';
import { CompactKpiCard } from '@/components/kpi-card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { Input } from '@/components/ui/input';
import { SensitiveData } from '@/components/sensitive-data';
import { fetchWIPReports, fetchProjects } from '@/lib/data-service';
import type { WipReportSummary, WipReportFilterParams, WipReportResponse } from '@/lib/data-service';
import type { AgeBand } from '@/components/billing/AgedWipStrip';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { WIPReport } from '@/types';
import type { PaginationMetadata } from '@/types/pagination';

import { useErrorHandler } from '@/hooks/use-error-handler';
import { useCompany } from '@/contexts/CompanyContext';
import { usePagination } from '@/hooks/use-pagination';
import { PaginationControls } from '@/components/pagination-controls';
import { exportWIPToExcel, fetchAllWipPages } from '@/lib/export-wip-excel';
import { getDataMode } from '@/lib/data-service';
import { useWipThresholds } from '@/hooks/use-wip-thresholds';
import { toast } from 'sonner';

/**
 * The "As at" date filter has no backend support yet — point-in-time WIP is not
 * calculated, so changing the date only triggers a refetch of today's figures.
 * Hidden from the UI until the backend logic lands; the state and filter
 * plumbing below are kept so re-enabling is a single flag flip.
 */
const SHOW_AS_AT_FILTER = false;

/**
 * The Month-End Checklist card is out of scope for R1. The derivation and card
 * markup below are kept so re-enabling is a single flag flip.
 */
const SHOW_MONTH_END_CHECKLIST = false;

const PROJECT_TYPE_OPTIONS = [
  { value: 'time-and-materials', label: 'T&M' },
  { value: 'fixed-price', label: 'Fixed Fee' },
  { value: 'retainer', label: 'Retainer' },
];

const PROJECT_TYPE_LABELS: Record<string, string> = {
  'time-and-materials': 'T&M',
  'fixed-price': 'Fixed Fee',
  'retainer': 'Retainer',
};

function ProjectTypeBadge({ type }: { type: WIPReport['projectType'] }) {
  if (!type) return null;
  const label = PROJECT_TYPE_LABELS[type] ?? type;
  if (type === 'fixed-price') {
    return <Badge variant="default" className="text-xs whitespace-nowrap">{label}</Badge>;
  }
  if (type === 'retainer') {
    return <Badge variant="secondary" className="text-xs whitespace-nowrap">{label}</Badge>;
  }
  // T&M
  return <Badge variant="outline" className="text-xs whitespace-nowrap">{label}</Badge>;
}

function isBillingDueThisMonth(wip: WIPReport): boolean {
  // % complete milestones: the trigger is the completion threshold, not a date.
  // They count as "due this month" when the threshold is met (i.e. they are actionable now).
  if (wip.nextBillingEventTriggerType === 'percent-complete') {
    const thresholdMet =
      wip.nextBillingEventTriggerPercent != null &&
      (wip.projectType === 'fixed-price'); // completionPercent is on the project, not WIPReport — treat "has a due % milestone" as actionable
    // More precisely: the mock data only sets nextBillingEventTriggerType when the event is the next one;
    // readyToBill already captures whether it's actually due. Use that as the proxy.
    return thresholdMet && wip.readyToBill;
  }
  // Date-based milestone: check if the event date falls in the current calendar month
  if (!wip.nextBillingEvent) return false;
  const d = new Date(wip.nextBillingEvent);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export function WIPReportingPage() {
  const [searchParams] = useSearchParams();
  // Read ?managerId= from URL (passed by homepage PM quick link).
  // We can't resolve a name until data loads, so we store the raw ID and seed
  // pmFilter in a useEffect once wipReports is populated.
  const initManagerId = searchParams.get('managerId') ? Number(searchParams.get('managerId')) : null;
  const initReportProjectId = searchParams.get('reportProjectId');
  const initAgingBand = (searchParams.get('agingBand') as AgeBand | null);

  const { thresholds: wipThresholds } = useWipThresholds();

  const [loading, setLoading] = useState(true);
  const [wipReports, setWipReports] = useState<WIPReport[]>([]);
  const [projectTypeFilter, setProjectTypeFilter] = useState<string[]>([]);
  const [billableFilter, setBillableFilter] = useState<string>('all');
  const [asAtDate, setAsAtDate] = useState<Date>(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [pmFilter, setPmFilter] = useState<string[]>([]);
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
  const [summary, setSummary] = useState<WipReportSummary | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [agingBandFilter, setAgingBandFilter] = useState<AgeBand | null>(initAgingBand);
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { currentCompany } = useCompany();
  const { currentPage, pageSize, setPage, setPageSize, resetPagination } = usePagination({
    defaultPageSize: 25,
    persistInUrl: false,
  });

  // Deep-link: fetch project code for search filter
  useEffect(() => {
    if (initReportProjectId) {
      import('@/lib/data-service').then(({ fetchProjectById }) => {
        fetchProjectById(Number(initReportProjectId)).then((p) => {
          if (p) {
            const code = (p as any).code || (p as any).name || '';
            setSearchTerm(code);
          }
        }).catch(() => { /* ignore */ });
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build filter params from current state
  const buildFilterParams = useCallback((): WipReportFilterParams => {
    const filters: WipReportFilterParams = {};
    if (projectTypeFilter.length === 1) {
      filters.projectType = projectTypeFilter[0];
    }
    if (pmFilter.length === 1) {
      filters.pmName = pmFilter[0];
    }
    if (billableFilter !== 'all') {
      filters.billable = billableFilter;
    }
    if (searchTerm.trim()) {
      filters.search = searchTerm.trim();
    }
    return filters;
  }, [projectTypeFilter, pmFilter, billableFilter, searchTerm]);

  // Fetch paginated data from the server
  const fetchData = useCallback(async (page: number, size: number, filters?: WipReportFilterParams) => {
    setLoading(true);
    try {
      const result = await fetchWIPReports('wip-reporting', { page, pageSize: size }, filters);
      if (result && 'data' in result && 'pagination' in result) {
        const response = result as WipReportResponse;
        setWipReports(response.data);
        setPagination(response.pagination);
        setSummary(response.summary);
      } else {
        // Fallback for non-paginated response (e.g. demo mode without pagination)
        setWipReports(result as WIPReport[]);
        setPagination(null);
        setSummary(null);
      }
    } catch (error) {
      handleApiError(error, '/api/reports/wip', 'GET');
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  // Initial load and refetch on company change
  useEffect(() => {
    fetchData(currentPage, pageSize, buildFilterParams());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id]);

  // Refetch when page or pageSize changes
  useEffect(() => {
    fetchData(currentPage, pageSize, buildFilterParams());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  // Debounced refetch on search term changes
  useEffect(() => {
    const handler = setTimeout(() => {
      resetPagination();
      fetchData(1, pageSize, buildFilterParams());
    }, 300);
    return () => clearTimeout(handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Refetch on filter changes (reset to page 1)
  useEffect(() => {
    resetPagination();
    fetchData(1, pageSize, buildFilterParams());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectTypeFilter, pmFilter, billableFilter, asAtDate]);

  // Seed pmFilter from ?managerId= once WIP data is loaded.
  // WIPReport rows carry pmName (string) but not pmId. We fetch projects once to
  // resolve managerId → project → pmName, then seed the filter. This runs only
  // once (when wipReports first populates) and only if managerId is in the URL.
  useEffect(() => {
    if (!initManagerId || loading || wipReports.length === 0) return;
    fetchProjects('projects', undefined, { pmId: initManagerId })
      .then(matchedProjects => {
        if (matchedProjects.length === 0) return;
        // Find a WIP row for one of the matched projects to get the pmName string
        const matchedIds = new Set(matchedProjects.map((p: { id: number }) => p.id));
        const match = wipReports.find(w => matchedIds.has(w.projectId) && w.pmName);
        if (match?.pmName) setPmFilter([match.pmName]);
      })
      .catch(() => { /* silent — filter just won't be pre-seeded */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, wipReports]);

  // Billing Due This Month banner data
  const billingDueThisMonth = useMemo(() => {
    const dueRows = wipReports.filter(w => isBillingDueThisMonth(w));
    const total = dueRows.reduce((sum, w) => sum + (w.nextBillingEventAmount ?? 0), 0);
    return { count: dueRows.length, total };
  }, [wipReports]);

  // PM options — derived from loaded data, deduplicated, sorted alphabetically
  const pmOptions = useMemo(() => {
    const names = wipReports
      .map(w => w.pmName)
      .filter((n): n is string => !!n);
    return Array.from(new Set(names)).sort();
  }, [wipReports]);

  // Summary figures — sourced from API summary (reflects full filtered dataset, not just current page)
  const totalWIP = summary?.totalWip ?? 0;
  const readyToBillAmount = summary?.readyToBillAmount ?? 0;
  const readyToBillCount = summary?.readyToBillCount ?? 0;
  const totalUnderBilling = summary?.totalUnderBilling ?? 0;
  const totalOverBilling = summary?.totalOverBilling ?? 0;
  const totalWrittenOff = summary?.totalWrittenOff ?? 0;

  // ── F34: Dynamic Month-End Checklist ─────────────────────────────────────
  // Derived from wipReports — same logic as Finance dashboard F33 widget,
  // using only data available on the WIP Reporting page.
  const readyToBillRows = wipReports.filter(w => w.readyToBill);
  const nowMs = Date.now();
  const hasWipOver60        = wipReports.some(r => (r.agingDays ?? 0) > wipThresholds.agedWipDaysThreshold);
  const hasReadyToBill      = readyToBillRows.length > 0;
  const hasOverdueMilestone = wipReports.some(r => r.nextBillingEvent && new Date(r.nextBillingEvent).getTime() < nowMs);
  const hasOverBilling      = totalOverBilling > 0;
  const hasWrittenOff       = totalWrittenOff > 0;

  type ChecklistState = 'complete' | 'action' | 'blocked';
  interface WipChecklistItem { label: string; state: ChecklistState; detail?: string }
  const wipChecklistItems: WipChecklistItem[] = useMemo(() => [
    {
      label: 'Review all WIP amounts',
      state: hasWipOver60 ? 'action' : 'complete',
      detail: hasWipOver60
        ? `${wipReports.filter(r => (r.agingDays ?? 0) > wipThresholds.agedWipDaysThreshold).length} project(s) with WIP aged ${wipThresholds.agedWipDaysThreshold}+ days`
        : undefined,
    },
    {
      label: 'Process ready-to-bill projects',
      state: hasReadyToBill ? 'action' : 'complete',
      detail: hasReadyToBill ? `${readyToBillRows.length} project(s) ready to raise` : undefined,
    },
    {
      label: 'Resolve overdue billing milestones',
      state: hasOverdueMilestone ? 'action' : 'complete',
      detail: hasOverdueMilestone
        ? `${wipReports.filter(r => r.nextBillingEvent && new Date(r.nextBillingEvent).getTime() < nowMs).length} overdue milestone(s)`
        : undefined,
    },
    {
      label: 'Clear over-billing',
      state: hasOverBilling ? 'action' : 'complete',
      detail: hasOverBilling
        ? `${formatCurrency(totalOverBilling)} billed ahead of completion`
        : undefined,
    },
    {
      label: 'Review write-offs',
      state: hasWrittenOff ? 'action' : 'complete',
      detail: hasWrittenOff ? `${formatCurrency(totalWrittenOff)} written off MTD` : undefined,
    },
    {
      label: 'Reconcile with Finance system',
      state: 'blocked' as ChecklistState,
      detail: 'Verify in General Settings once connected',
    },
    {
      label: 'Verify timesheet approvals',
      state: 'blocked' as ChecklistState,
      detail: 'Verify in Timesheets once connected',
    },
  ], [
    hasWipOver60, hasReadyToBill, hasOverdueMilestone, hasOverBilling, hasWrittenOff,
    wipReports, readyToBillRows.length, totalOverBilling, totalWrittenOff,
  ]);

  const wipChecklistComplete = wipChecklistItems.filter(i => i.state === 'complete').length;
  const wipChecklistProgress = Math.round((wipChecklistComplete / wipChecklistItems.length) * 100);
  // ─────────────────────────────────────────────────────────────────────────

  const activeFilterCount = (projectTypeFilter.length > 0 ? 1 : 0) + (pmFilter.length > 0 ? 1 : 0) + (billableFilter !== 'all' ? 1 : 0) + (searchTerm.trim() ? 1 : 0) + (agingBandFilter ? 1 : 0);

  function clearAllFilters() {
    setProjectTypeFilter([]);
    setPmFilter([]);
    setBillableFilter('all');
    setAsAtDate(new Date());
    setSearchTerm('');
    setAgingBandFilter(null);
  }

  const getAgingBadge = (days: number) => {
    if (days <= 30) return <Badge variant="success">0-30 days</Badge>;
    if (days <= 60) return <Badge variant="warning">31-60 days</Badge>;
    if (days <= 90) return <Badge variant="warning" accent="orange">61-90 days</Badge>;
    if (days <= 120) return <Badge variant="destructive" className="bg-error-100 text-error-600 border-error-200">91-120 days</Badge>;
    return <Badge variant="destructive">121+ days</Badge>;
  };

  // ── Column definitions — memoised so TanStack Table doesn't reinitialise ──
  const columns = useMemo<ColumnDef<WIPReport, unknown>[]>(() => {
    const cols: ColumnDef<WIPReport, unknown>[] = [
      {
        id: 'projectName',
        header: 'Project',
        accessorKey: 'projectName',
        cell: ({ row }) => <div className="font-medium">{row.original.projectName}</div>,
      },
      {
        id: 'clientName',
        header: 'Client',
        accessorKey: 'clientName',
      },
    ];

    // Only show PM column when multiple PMs are selected (or no filter)
    if (pmFilter.length !== 1) {
      cols.push({
        id: 'pmName',
        header: 'Project Manager',
        accessorKey: 'pmName',
        cell: ({ row }) => <span className="text-sm">{row.original.pmName ?? '—'}</span>,
      });
    }

    cols.push(
      {
        id: 'billable',
        header: 'Billable',
        cell: ({ row }) => {
          const isBillable = row.original.billable ?? true;
          return isBillable
            ? <Badge variant="outline" className="text-xs text-emerald-700 bg-emerald-50 border-emerald-200">Billable</Badge>
            : <Badge variant="outline" className="text-xs text-muted-foreground">Non-billable</Badge>;
        },
      },
      {
        id: 'projectType',
        header: 'Type',
        accessorKey: 'projectType',
        cell: ({ row }) => <ProjectTypeBadge type={row.original.projectType} />,
      },
      {
        id: 'costsIncurred',
        header: 'Costs Incurred',
        accessorKey: 'costsIncurred',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <SensitiveData>{formatCurrency(row.original.costsIncurred)}</SensitiveData>
        ),
      },
      {
        id: 'profitEarned',
        header: 'Profit Earned',
        accessorKey: 'profitEarned',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <SensitiveData>{formatCurrency(row.original.profitEarned)}</SensitiveData>
        ),
      },
      {
        id: 'amountBilled',
        header: 'Amount Billed',
        accessorKey: 'amountBilled',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <SensitiveData>{formatCurrency(row.original.amountBilled)}</SensitiveData>
        ),
      },
      {
        id: 'wipAmount',
        header: 'WIP Amount',
        accessorKey: 'wipAmount',
        meta: { align: 'right' },
        cell: ({ row }) => {
          const v = row.original.wipAmount;
          return (
            <SensitiveData>
              <span className={`font-semibold ${v > 0 ? 'text-warning-600' : v < 0 ? 'text-error-600' : 'text-success-600'}`}>
                {formatCurrency(v)}
              </span>
            </SensitiveData>
          );
        },
      },
      {
        id: 'nextBillingEvent',
        header: () => (
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            Next Billing
          </div>
        ),
        accessorKey: 'nextBillingEvent',
        cell: ({ row }) => {
          const wip = row.original;
          if (wip.nextBillingEventTriggerType === 'percent-complete') {
            return (
              <div className="space-y-0.5">
                <div className="text-sm font-medium">At {wip.nextBillingEventTriggerPercent}% complete</div>
                {wip.nextBillingEvent && (
                  <div className="text-xs text-muted-foreground">by {formatDate(wip.nextBillingEvent)}</div>
                )}
                {wip.nextBillingEventAmount != null && (
                  <SensitiveData>
                    <div className="text-xs text-muted-foreground">{formatCurrency(wip.nextBillingEventAmount)}</div>
                  </SensitiveData>
                )}
              </div>
            );
          }
          if (wip.nextBillingEvent) {
            return (
              <div className="space-y-0.5">
                <div className="text-sm">{formatDate(wip.nextBillingEvent)}</div>
                {wip.nextBillingEventAmount != null && (
                  <SensitiveData>
                    <div className="text-xs text-muted-foreground">{formatCurrency(wip.nextBillingEventAmount)}</div>
                  </SensitiveData>
                )}
              </div>
            );
          }
          return <span className="text-muted-foreground text-sm">—</span>;
        },
      },
      {
        id: 'writtenOffAmount',
        header: 'Written Off',
        accessorKey: 'writtenOffAmount',
        meta: { align: 'right' },
        cell: ({ row }) => {
          const v = row.original.writtenOffAmount ?? 0;
          return (
            <SensitiveData>
              {v > 0 ? (
                <span className="text-destructive font-medium">−{formatCurrency(v)}</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </SensitiveData>
          );
        },
      },
      {
        id: 'agingDays',
        header: 'Aging',
        accessorKey: 'agingDays',
        cell: ({ row }) => getAgingBadge(row.original.agingDays),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'readyToBill',
        cell: ({ row }) => {
          const wip = row.original;
          if (wip.readyToBill) {
            return (
              <Badge variant="success">
                <CheckCircle2 className="mr-1 h-3 w-3" />
                Ready to Bill
              </Badge>
            );
          }
          if (wip.overBilling > 0) return <Badge variant="destructive">Over Billed</Badge>;
          return <Badge variant="secondary">In Progress</Badge>;
        },
      },
    );

    return cols;
  }, [pmFilter.length]);

  // Apply client-side sort from columnSorts state (sorting within the current page)
  const sortedReports = useMemo(() => {
    // Apply aging band filter first (client-side)
    let base = wipReports;
    if (agingBandFilter) {
      base = wipReports.filter(r => {
        const days = r.agingDays ?? 0;
        switch (agingBandFilter) {
          case '0-30':   return days >= 0  && days <= 30;
          case '31-60':  return days >= 31 && days <= 60;
          case '61-90':  return days >= 61 && days <= 90;
          case '91-120': return days >= 91 && days <= 120;
          case '121+':   return days > 120;
          default:       return true;
        }
      });
    }
    if (columnSorts.length === 0) return base;
    return [...base].sort((a, b) => {
      for (const sort of columnSorts) {
        const key = sort.columnId as keyof WIPReport;
        const aVal = a[key];
        const bVal = b[key];
        const dir = sort.direction === 'asc' ? 1 : -1;
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return dir;
        if (bVal == null) return -dir;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          if (aVal !== bVal) return (aVal - bVal) * dir;
        } else {
          const cmp = String(aVal).localeCompare(String(bVal));
          if (cmp !== 0) return cmp * dir;
        }
      }
      return 0;
    });
  }, [wipReports, columnSorts, agingBandFilter]);

  // ── Excel export ─────────────────────────────────────────────────────────
  const handleExportExcel = useCallback(async () => {
    if (isExporting) return;
    setIsExporting(true);
    toast.info('Export started — your download will begin shortly.');

    try {
      const isLive = getDataMode() === 'live';
      let exportData: WIPReport[];

      if (isLive) {
        exportData = await fetchAllWipPages(buildFilterParams());
      } else {
        const allData = await fetchWIPReports('wip-reporting', undefined, buildFilterParams());
        exportData = Array.isArray(allData) ? allData : allData.data;
      }

      await exportWIPToExcel(exportData);
      toast.success(`Export complete — ${exportData.length} projects downloaded.`);
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [isExporting, buildFilterParams]);

  return (
    <div className="space-y-6">
      <PageHeaderBlock
        title="WIP"
        subtitle="Work In Progress analysis for month-end close"
        size={20}
      />

      {/* Billing Due This Month Banner */}
      {!loading && billingDueThisMonth.count > 0 && (
        <div className="flex items-center justify-between gap-4 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3">
          <div className="flex items-center gap-3">
            <Zap className="h-5 w-5 shrink-0 text-warning-600" />
            <p className="text-sm font-medium text-warning-900">
              <span className="font-semibold">ACTION REQUIRED</span>
              {' — '}
              {billingDueThisMonth.count} billing event{billingDueThisMonth.count !== 1 ? 's' : ''} due this month totalling{' '}
              <SensitiveData>
                <span className="font-semibold">{formatCurrency(billingDueThisMonth.total)}</span>
              </SensitiveData>
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="shrink-0 border-warning-300 text-warning-800 hover:bg-warning-100"
            onClick={() => setProjectTypeFilter(['fixed-price'])}
          >
            View Due Items
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4" data-tour="wip-summary-cards">
        {/* Total WIP */}
        <CompactKpiCard
          title="Total WIP"
          value={<SensitiveData>{formatCurrency(totalWIP)}</SensitiveData>}
          description="Unbilled work value"
          icon={Wallet}
          accentVariant="info"
        />

        {/* Ready to Bill */}
        <CompactKpiCard
          title="Ready to Bill"
          value={<SensitiveData>{formatCurrency(readyToBillAmount)}</SensitiveData>}
          description={`${readyToBillCount} project${readyToBillCount !== 1 ? 's' : ''} ready for invoicing`}
          icon={CheckCircle2}
          accentVariant="success"
        />

        {/* Combined Under Billing + Over Billing → Billing Variance */}
        <CompactKpiCard
          title="Billing Variance"
          value={<SensitiveData>{formatCurrency(totalUnderBilling)}</SensitiveData>}
          description={<span className="flex flex-col gap-0.5"><span>Under billed</span><span className="border-t border-border pt-0.5 font-semibold text-error-600"><SensitiveData>{formatCurrency(totalOverBilling)}</SensitiveData> over billed</span></span>}
          icon={TrendingDown}
          accentVariant="warning"
        />

        {/* ── Feature 4: Written Off summary card ──────────────────────────
            TODO (Engineering — Feature 4): Replace totalWrittenOff with a
            real aggregate from GET /api/wip-reports/summary */}
        <CompactKpiCard
          title="Written Off"
          value={<SensitiveData>{formatCurrency(totalWrittenOff)}</SensitiveData>}
          description="WIP written off this period"
          icon={PenLine}
          accentVariant={totalWrittenOff > 0 ? 'error' : 'info'}
        />
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3" data-tour="wip-filters">
        {/* As at date picker */}
        {SHOW_AS_AT_FILTER && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">As at:</span>
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-40 justify-between font-normal">
                  {asAtDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  <ChevronDownIcon className="h-4 w-4 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto overflow-hidden p-0" align="start">
                <CalendarComponent
                  mode="single"
                  selected={asAtDate}
                  captionLayout="dropdown"
                  startMonth={new Date(1900, 0)}
                  endMonth={new Date(2100, 11)}
                  onSelect={(date) => {
                    if (date) setAsAtDate(date);
                    setDatePickerOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        {/* Project type filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Project type:</span>
          <MultiSelect
            options={PROJECT_TYPE_OPTIONS}
            value={projectTypeFilter}
            onValueChange={setProjectTypeFilter}
            placeholder="All types"
            size="sm"
            className="w-44"
            summarizeAfter={1}
          />
        </div>

        {/* Project Manager filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Project Manager:</span>
          <MultiSelect
            options={pmOptions.map(pm => ({ value: pm, label: pm }))}
            value={pmFilter}
            onValueChange={setPmFilter}
            placeholder="All Project Managers"
            size="sm"
            className="w-52"
            summarizeAfter={1}
          />
        </div>

        {/* Billable filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">Billable:</span>
          <Select value={billableFilter} onValueChange={setBillableFilter}>
            <SelectTrigger className="h-8 w-36 text-sm">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="billable">Billable</SelectItem>
              <SelectItem value="non-billable">Non-billable</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Aging band filter pill (set from dashboard deep-link) */}
        {agingBandFilter && (
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Aging:</span>
            <Badge
              variant="secondary"
              className="gap-1 cursor-pointer hover:bg-destructive/10 hover:text-destructive"
              onClick={() => setAgingBandFilter(null)}
            >
              {agingBandFilter} days
              <X className="h-3 w-3" />
            </Badge>
          </div>
        )}

        {/* Clear filters */}
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-muted-foreground hover:text-foreground gap-1.5">
            Clear filters
            <Badge variant="secondary" className="text-xs px-1.5 py-0 h-4">{activeFilterCount}</Badge>
          </Button>
        )}

        {/* Export to Excel */}
        <div className="ml-auto">
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={handleExportExcel}
            disabled={isExporting || sortedReports.length === 0}
            title="Export to Excel"
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* WIP Table */}
      <Card data-tour="wip-table">
        <CardHeader>
          <CardTitle>WIP Analysis by Project</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {/* Search */}
          <div className="px-6 pt-4 pb-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by project name or code..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
          <DataTableEnhanced
            columns={columns}
            data={sortedReports}
            loading={loading}
            loadingRowCount={10}
            columnSorts={columnSorts}
            onColumnSortChange={(sorts) => {
              setColumnSorts(prev => cycleColumnSort(prev, sorts[sorts.length - 1]?.columnId ?? ''));
            }}
            getRowId={row => String(row.projectId)}
            className="rounded-none border-0 border-t"
            emptyMessage={
              searchTerm
                ? <span>No projects match <strong>{searchTerm}</strong>.</span>
                : activeFilterCount > 0 ? (
                  <span>
                    No projects match the selected filters.{' '}
                    <button onClick={clearAllFilters} className="underline hover:no-underline">Clear filters</button>
                  </span>
                ) : 'No WIP data found.'
            }
            paginationContent={
              !loading && pagination && pagination.totalItems > 0 ? (
                <PaginationControls
                  pagination={pagination}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                  showPageSizeSelector={true}
                />
              ) : undefined
            }
          />
        </CardContent>
      </Card>

      {/* Insights */}
      <div className={`grid gap-4 ${SHOW_MONTH_END_CHECKLIST ? 'md:grid-cols-2' : ''}`}>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">💡 WIP Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="h-2 w-2 rounded-full bg-success-600 mt-2" />
              <div>
                <p className="text-sm font-medium">Ready for Invoicing</p>
                <p className="text-xs text-muted-foreground">
                  {readyToBillCount} project{readyToBillCount !== 1 ? 's are' : ' is'} ready to bill
                  {readyToBillAmount > 0 && (
                    <>
                      {' '}totalling{' '}
                      <SensitiveData>
                        {formatCurrency(readyToBillAmount)}
                      </SensitiveData>
                    </>
                  )}
                </p>
              </div>
            </div>
            {billingDueThisMonth.count > 0 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-warning-500 mt-2" />
                <div>
                  <p className="text-sm font-medium">Billing Events Due This Month</p>
                  <p className="text-xs text-muted-foreground">
                    {billingDueThisMonth.count} billing event{billingDueThisMonth.count !== 1 ? 's' : ''} totalling{' '}
                    <SensitiveData>
                      {formatCurrency(billingDueThisMonth.total)}
                    </SensitiveData>
                  </p>
                </div>
              </div>
            )}
            {totalUnderBilling > 50000 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-warning-600 mt-2" />
                <div>
                  <p className="text-sm font-medium">Under Billing Alert</p>
                  <p className="text-xs text-muted-foreground">
                    <SensitiveData>
                      {formatCurrency(totalUnderBilling)}
                    </SensitiveData>{' '}
                    in unbilled work - consider invoicing
                  </p>
                </div>
              </div>
            )}
            {totalOverBilling > 0 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-error-600 mt-2" />
                <div>
                  <p className="text-sm font-medium">Over Billing Risk</p>
                  <p className="text-xs text-muted-foreground">
                    <SensitiveData>
                      {formatCurrency(totalOverBilling)}
                    </SensitiveData>{' '}
                    billed ahead of work completion
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {SHOW_MONTH_END_CHECKLIST && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Month-End Checklist</CardTitle>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {wipChecklistComplete} of {wipChecklistItems.length} tasks complete
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Progress bar */}
            <div className="space-y-1.5">
              <Progress value={wipChecklistProgress} className="h-1.5" />
              <p className={`text-xs font-medium ${
                wipChecklistProgress === 100
                  ? 'text-success-600'
                  : wipChecklistProgress >= 50
                    ? 'text-blue-600'
                    : 'text-warning-600'
              }`}>
                {wipChecklistProgress === 100
                  ? '✅ All close tasks complete — ready for sign-off'
                  : wipChecklistProgress >= 50
                    ? `Getting close — ${wipChecklistItems.length - wipChecklistComplete} item${wipChecklistItems.length - wipChecklistComplete !== 1 ? 's' : ''} remaining`
                    : 'Month-end close in progress'}
              </p>
            </div>

            {/* Checklist items */}
            <div className="space-y-2 pt-1">
              {wipChecklistItems.map((item) => {
                const isComplete = item.state === 'complete';
                const isBlocked  = item.state === 'blocked';
                return (
                  <div key={item.label} className="flex items-start gap-2.5">
                    <div className="mt-0.5 shrink-0">
                      {isComplete ? (
                        <CheckCircle2 className="h-4 w-4 text-success-600" />
                      ) : isBlocked ? (
                        <Lock className="h-4 w-4 text-muted-foreground/50" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-warning-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm leading-snug ${isComplete ? 'text-muted-foreground line-through decoration-muted-foreground/40' : isBlocked ? 'text-muted-foreground/60' : 'text-foreground font-medium'}`}>
                        {item.label}
                      </p>
                      {item.detail && (
                        <p className="text-xs text-muted-foreground mt-0.5">{item.detail}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </div>
  );
}
